import React from 'react';
import is from 'utils/is';
import toArray from 'utils/to-array';
import type { StyledElement, TestAutomation } from 'contracts';

import Html from '../../html';
import LinkifyText from '../../linkify-text';

import styles from './row.module.scss';

type RowOnChange<T> = (value: T, valid: boolean) => void;

interface RowElement extends StyledElement, TestAutomation {
  label?: string;
  hint?: React.ReactNode;
  warning?: boolean | string | null | undefined;
  children: React.ReactNode;
  hidden?: boolean;
  column?: boolean;
}

const Row: React.FunctionComponent<RowElement> = (props) => {
  const { label, hint, warning, hidden, className, children, column, testId } = props;
  const ref = React.useRef<HTMLDivElement>(undefined);

  React.useLayoutEffect(() => {
    if (!ref.current) return;

    ref.current.style.maxHeight = '';

    const height = ref.current?.clientHeight;

    if (!is.bool(hidden) || !height) return;

    ref.current.style.maxHeight = `${height}px`;
  }, [hidden, warning, hint, children]);

  return (
    <Html.div
      testId={testId}
      className={[!column && 'mb-4', !column && 'row', styles.row, hidden && styles.rowHidden, ...toArray(className)]}
      hidden={hidden}
      ref={ref}
    >
      <Html.label className={['col-12', 'align-items-center', 'mb-1', styles.label]} typography="body2">
        {label}
      </Html.label>
      <Html.div
        className={[
          'col-12',
          'align-items-center',
          (Boolean(hint) || (Boolean(warning) && !is.bool(warning))) && 'mb-1',
        ]}
      >
        {children}
      </Html.div>
      {Boolean(hint) && (
        <Html.div
          testId={testId && `${testId}-hint`}
          className={[
            'col-12',
            'align-items-center',
            Boolean(warning) && !is.bool(warning) && 'mb-1',
            styles.hint,
            Boolean(warning) && styles.warning,
          ]}
          typography="caption"
        >
          {hint}
        </Html.div>
      )}
      {Boolean(warning) && !is.bool(warning) && (
        <LinkifyText
          testId={testId && `${testId}-warning`}
          text={warning!.toString()}
          className={['col-12', 'align-items-center', styles.hint, styles.warning]}
        />
      )}
    </Html.div>
  );
};

export type { RowElement, RowOnChange };
export default Row;
